import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LogOut, ShieldCheck, User, BookOpen, Users2, Trophy } from "lucide-react";

export default function NavBar({ subtitle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const daysLeft = (() => {
    if (!user?.expiry_date) return null;
    const diff = Math.ceil((new Date(user.expiry_date) - new Date()) / 86400000);
    return diff;
  })();

  return (
    <nav className="fixed top-0 left-0 right-0 h-16 z-40 bg-slate-900/90 backdrop-blur-md border-b border-slate-800 px-6 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-home">
          <div className="w-9 h-9 rounded bg-blue-600 flex items-center justify-center font-heading font-black text-lg">CG</div>
          <div className="leading-tight">
            <div className="font-heading text-lg font-extrabold uppercase tracking-wider">Cricket Graphics <span className="text-blue-500">Studio</span></div>
            {subtitle && <div className="text-xs text-slate-400 -mt-0.5">{subtitle}</div>}
          </div>
        </Link>
      </div>
      <div className="flex items-center gap-4">
        {user?.role === "admin" ? (
          <span className="hidden sm:inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-600/15 text-blue-300 text-xs font-semibold border border-blue-800">
            <ShieldCheck size={14} /> Super Admin
          </span>
        ) : daysLeft !== null ? (
          <span
            data-testid="account-expiry-badge"
            className={`hidden sm:inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border ${
              daysLeft <= 3
                ? "bg-red-500/15 text-red-300 border-red-800"
                : daysLeft <= 7
                ? "bg-amber-500/15 text-amber-300 border-amber-800"
                : "bg-emerald-500/15 text-emerald-300 border-emerald-800"
            }`}>
            {daysLeft > 0 ? `${daysLeft} days left` : "Expired"}
          </span>
        ) : user ? (
          <span className="hidden sm:inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 text-slate-300 text-xs border border-slate-700">Unlimited</span>
        ) : null}
        {user && (
          <div className="hidden md:flex items-center gap-2 text-sm text-slate-300">
            <User size={16} className="text-slate-500" />
            <span data-testid="nav-user-email">{user.email}</span>
          </div>
        )}
        {user && user.role !== "admin" && (
          <>
            <Link to="/matches" data-testid="nav-matches-link"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-slate-800">
              Matches
            </Link>
            <Link to="/teams" data-testid="nav-teams-link"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-slate-800">
              <Users2 size={14}/> Teams
            </Link>
            <Link to="/tournaments" data-testid="nav-tournaments-link"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-slate-800">
              <Trophy size={14}/> Tournaments
            </Link>
          </>
        )}
        {user && (
          <Link to="/manual" data-testid="nav-manual-link"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm border border-slate-700 text-slate-200">
            <BookOpen size={14} /> Manual
          </Link>
        )}
        <button
          onClick={() => { logout(); navigate("/login"); }}
          data-testid="nav-logout-button"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm border border-slate-700"
        >
          <LogOut size={14} /> Logout
        </button>
      </div>
    </nav>
  );
}
